import { useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { ThemeContext, THEME_MODES, type ThemeMode } from './useTheme'

const STORAGE_KEY = 'lighthouse-theme'

const THEME_NAMES: Record<ThemeMode, string> = {
  light: 'Light',
  dark: 'Dark',
}

/** Retired modes that may still be sitting in a returning user's localStorage. */
const RETIRED_MODE_MAP: Record<string, ThemeMode> = {
  dusk: 'dark',
  'pro-light': 'light',
  'pro-dark': 'dark',
  cyberpunk: 'dark',
  forest: 'light',
  'forest-twilight': 'dark',
}

function isThemeMode(value: string): value is ThemeMode {
  return (THEME_MODES as readonly string[]).includes(value)
}

function getInitialTheme(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY)

  if (stored) {
    if (isThemeMode(stored)) return stored
    // Migrate a retired theme name to its nearest surviving mode rather than
    // silently ignoring it — a returning user keeps their light/dark intent.
    const migrated = RETIRED_MODE_MAP[stored]
    if (migrated) {
      localStorage.setItem(STORAGE_KEY, migrated)
      return migrated
    }
  }

  // Nothing usable stored: follow the OS preference.
  if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  return 'light'
}

/**
 * Applied synchronously (state initializer + setters), not only in an effect:
 * render-phase consumers like useThemeColors() read computed CSS variables via
 * getComputedStyle, so `data-theme` must be on <html> before the render that
 * reads it — an effect runs too late and charts would resolve the previous
 * mode's colors.
 *
 * This is also why tokens.css has no `prefers-color-scheme` block: `data-theme`
 * is always stamped before first paint, seeded from the OS preference above.
 */
function applyThemeToDom(mode: ThemeMode) {
  const root = document.documentElement
  // Retired `.theme-*` classes are swept once so a cached document can't keep
  // resolving an old palette.
  root.classList.remove(
    ...[...THEME_MODES, ...Object.keys(RETIRED_MODE_MAP)].map((t) => `theme-${t}`),
  )
  root.classList.add(`theme-${mode}`)
  root.setAttribute('data-theme', mode)
  // Lets the browser style form controls, scrollbars and caret to match.
  root.style.colorScheme = mode
}

interface ThemeContextProviderProps {
  children: ReactNode
}

export function ThemeContextProvider({ children }: ThemeContextProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const initial = getInitialTheme()
    applyThemeToDom(initial)
    return initial
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode)
  }, [mode])

  useEffect(() => {
    const favicon = document.getElementById('favicon') as HTMLLinkElement | null
    if (favicon) {
      favicon.href = '/brand/favicon-32.png'
    }
  }, [mode])

  // Backstop for the synchronous application above — idempotent; re-asserts the
  // attribute if anything external strips it.
  useEffect(() => {
    applyThemeToDom(mode)
  }, [mode])

  const setTheme = (newMode: ThemeMode) => {
    applyThemeToDom(newMode)
    setMode(newMode)
  }

  const toggleTheme = () => {
    setTheme(mode === 'light' ? 'dark' : 'light')
  }

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setTheme, themeName: THEME_NAMES[mode] }}>
      {children}
    </ThemeContext.Provider>
  )
}
